import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Real slide thumbnails in deck order. Arrow keys, Home and End move between slides.
 * @param {object} props - Props.
 * @param {Array<object>} props.slides - Slides (from slidesOf).
 * @param {Object<string, {thumb: string|null, png: string|null}>} props.images - Signed image URLs by slide key.
 * @param {string|null} props.selectedKey - Selected slide.
 * @param {function(string): void} props.onSelect - Select a slide.
 * @param {Object<string, number>} props.suggestionCounts - Suggested fields per slide key.
 * @param {string} [props.ratio] - CSS aspect ratio of the slides (the master's).
 * @returns {React.ReactElement}
 */
function DeckFilmstrip({ slides, images, selectedKey, onSelect, suggestionCounts, ratio = '16 / 9' }) {
  const itemRefs = useRef(new Map());

  // Keep the selected slide in view (selection also changes from the keyboard and the canvas).
  useEffect(() => {
    const node = itemRefs.current.get(selectedKey);
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedKey]);

  const move = (event, index) => {
    let next = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = Math.min(slides.length - 1, index + 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = Math.max(0, index - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = slides.length - 1;
    if (next === null) return;
    event.preventDefault();
    const key = slides[next].key;
    onSelect(key);
    itemRefs.current.get(key)?.focus();
  };

  return (
    <Box
      component="nav"
      aria-label="Slides"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        gap: 1.25,
        overflowX: { xs: 'auto', md: 'hidden' },
        overflowY: { xs: 'hidden', md: 'auto' },
        p: 1,
        maxHeight: { md: 'calc(100vh - 150px)' },
      }}
    >
      {slides.map((slide, index) => {
        const selected = slide.key === selectedKey;
        const thumb = images[slide.key]?.thumb || images[slide.key]?.png || null;
        const suggested = suggestionCounts[slide.key] || 0;
        const notes = [
          slide.hidden ? 'hidden in PowerPoint' : null,
          slide.excluded ? 'left out' : null,
          suggested ? `${suggested} suggestion${suggested === 1 ? '' : 's'} to review` : null,
        ].filter(Boolean);
        return (
          <Box
            key={slide.key}
            ref={(node) => {
              if (node) itemRefs.current.set(slide.key, node);
              else itemRefs.current.delete(slide.key);
            }}
            component="button"
            type="button"
            data-testid="deck-filmstrip-item"
            aria-current={selected ? 'true' : undefined}
            aria-label={`Slide ${slide.number}${notes.length ? `, ${notes.join(', ')}` : ''}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(slide.key)}
            onKeyDown={(event) => move(event, index)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              flex: 'none',
              width: { xs: 150, md: '100%' },
              p: 0.75,
              borderRadius: 'var(--q-radius-md)',
              bgcolor: selected ? 'var(--q-accent-selected)' : 'transparent',
              '&:hover': { bgcolor: selected ? 'var(--q-accent-selected)' : 'var(--q-hover)' },
              '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 1 },
            }}
          >
            <Typography component="span" sx={{ width: 18, flex: 'none', fontSize: 12, fontWeight: 600, color: 'text.secondary', textAlign: 'right', pt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
              {slide.number}
            </Typography>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: ratio,
                  borderRadius: '3px',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: selected ? 'var(--q-accent)' : 'divider',
                  bgcolor: 'var(--q-subtle)',
                  opacity: slide.excluded || slide.hidden ? 0.5 : 1,
                }}
              >
                {thumb ? (
                  <Box component="img" src={thumb} alt="" draggable={false} sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                ) : (
                  <Box aria-hidden="true" sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'text.secondary' }}>
                    {slide.hidden ? 'Hidden' : 'No image yet'}
                  </Box>
                )}
              </Box>
              <Box aria-hidden="true" sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                {slide.excluded && <Badge tone="neutral">Left out</Badge>}
                {suggested > 0 && <Badge tone="accent">{suggested}</Badge>}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Small pill on a thumbnail.
 * @param {object} props - Props.
 * @param {'accent'|'neutral'} props.tone - Colour.
 * @param {React.ReactNode} props.children - Label.
 * @returns {React.ReactElement}
 */
function Badge({ tone, children }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        height: 18,
        minWidth: 18,
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        color: tone === 'accent' ? 'var(--q-on-accent)' : 'var(--q-text)',
        bgcolor: tone === 'accent' ? 'var(--q-accent)' : 'var(--q-surface)',
        border: tone === 'accent' ? 'none' : '1px solid var(--q-border)',
      }}
    >
      {children}
    </Box>
  );
}

export default DeckFilmstrip;
